// Real /statements list — GET /api/v1/statements (SMA monthly statements) plus,
// for a portal-invited FUND investor (role=client), their FUND statements via
// GET /api/v1/fund-investor-statements (16.e.3.5b.2). The two are distinct record
// types (SMA Statement vs FundInvestorStatement) so they render as separate
// sections; a given investor typically has one or the other. Manager/auditor keep
// the SMA-only view here — the manager issues + reviews fund statements on the
// per-fund /fund/:id/statements surface (16.e.3.5b.1).

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader, Pill } from '../../components/shell';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePolling } from '../../lib/usePolling';

// Statements only change at period close — slow poll.
const STATEMENTS_POLL_MS = 5 * 60_000;

interface StatementRow {
  id: string;
  client_id: string;
  client_name: string;
  period_label: string;
  version: number;
  issued_at: string | null;
  status_label: 'issued' | 'correction' | 'superseded';
  superseded: boolean;
  superseded_by_id: string | null;
  note: string;
}

interface FundStatementRow {
  id: string;
  fund_id: string;
  fund_name: string;
  investor_name: string;
  period_label: string;
  version: number;
  issued_at: string | null;
  status_label: 'issued' | 'correction' | 'superseded';
  superseded: boolean;
  superseded_by_id: string | null;
  base_currency: string;
  pdf_available: boolean;
  note: string;
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  }) : '—';

export default function Statements() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: rows, error } = usePolling<StatementRow[]>(
    (signal) => api<StatementRow[]>('/statements/', { signal }),
    { intervalMs: STATEMENTS_POLL_MS },
  );

  // Fund-investor statements: only a portal-invited fund investor (role=client)
  // sees their own here; the endpoint is investor-scoped server-side.
  const isInvestor = user?.role === 'client';
  const { data: fundRows } = usePolling<FundStatementRow[]>(
    (signal) => api<FundStatementRow[]>('/fund-investor-statements/', { signal }),
    { intervalMs: STATEMENTS_POLL_MS, enabled: isInvestor },
  );

  useEffect(() => {
    setErrorMsg(error ? 'Failed to load statements.' : null);
  }, [error]);

  if (errorMsg) {
    return <div className="view container"><p className="serif-body" style={{ color: 'var(--accent)' }}>{errorMsg}</p></div>;
  }
  if (!rows) {
    return (
      <div className="view container">
        <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
          Loading…
        </p>
      </div>
    );
  }

  const fund = fundRows || [];
  const hasFund = fund.length > 0;
  const hasSMA = rows.length > 0;
  const issuedCount = rows.filter(s => !s.superseded).length;
  const fundLive = fund.filter(s => !s.superseded).length;

  return (
    <div className="view container">
      <PageHeader
        kicker="STATEMENTS"
        title={<><em>Monthly</em> statements.</>}
        meta={
          <>
            <span className="label-s">
              {hasFund ? `${fundLive} FUND` : `${issuedCount} ISSUED`} · {rows.length + fund.length} TOTAL
            </span>
            <span className="label-s">IMMUTABLE ONCE PUBLISHED</span>
          </>
        }
      />

      {/* ---- Fund statements (portal-invited fund investor) ---- */}
      {hasFund && (
        <section style={{ marginBottom: hasSMA ? 64 : 0 }}>
          <div className="label" style={{ marginBottom: 4 }}>FUND STATEMENTS</div>
          <div style={{ borderTop: '1px solid var(--ink)' }}>
            {fund.map((st, i) => (
              <div
                key={st.id}
                style={{
                  padding: '28px 0',
                  borderBottom: i === fund.length - 1 ? '1px solid var(--ink)' : 'var(--hairline)',
                  display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 32,
                  alignItems: 'baseline',
                  opacity: st.superseded ? 0.5 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                <div>
                  <div className="display-m" style={{ fontSize: 32, color: st.superseded ? 'var(--ink-3)' : 'var(--ink)' }}>
                    {st.period_label}
                  </div>
                  <div className="serif-body" style={{ fontSize: 14, marginTop: 6, fontStyle: 'italic', color: 'var(--ink-2)' }}>
                    {st.fund_name}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span className="label-s">ISSUED</span>
                  <span className="num" style={{ fontSize: 13 }}>{fmtDate(st.issued_at)}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span className="label-s">VERSION</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="num" style={{ fontSize: 13 }}>v{st.version}</span>
                    {st.status_label === 'correction' && <Pill tone="accent">CORRECTION</Pill>}
                    {st.superseded && <Pill tone="warn">SUPERSEDED</Pill>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 24 }}>
                  {st.pdf_available ? (
                    <a className="btn-link"
                       href={`/api/v1/fund-investor-statements/${st.id}/pdf`}
                       download
                       style={{ textDecoration: 'none' }}>
                      Download PDF ↓
                    </a>
                  ) : (
                    <span className="label-s" style={{ color: 'var(--ink-3)' }}>RENDERING…</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---- SMA monthly statements ---- */}
      {hasSMA && (
        <section>
          {hasFund && <div className="label" style={{ marginBottom: 4 }}>SEPARATELY MANAGED ACCOUNT</div>}
          <div style={{ borderTop: '1px solid var(--ink)' }}>
            {rows.map((st, i) => (
              <div
                key={st.id}
                style={{
                  padding: '28px 0',
                  borderBottom: i === rows.length - 1 ? '1px solid var(--ink)' : 'var(--hairline)',
                  display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 32,
                  alignItems: 'baseline',
                  opacity: st.superseded ? 0.5 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                <div>
                  <div className="display-m" style={{ fontSize: 32, color: st.superseded ? 'var(--ink-3)' : 'var(--ink)' }}>
                    {st.period_label}
                  </div>
                  {st.note && (
                    <div className="serif-body" style={{ fontSize: 14, marginTop: 6, fontStyle: 'italic', color: 'var(--ink-2)' }}>
                      {st.note}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span className="label-s">ISSUED</span>
                  <span className="num" style={{ fontSize: 13 }}>{fmtDate(st.issued_at)}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span className="label-s">VERSION</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="num" style={{ fontSize: 13 }}>v{st.version}</span>
                    {st.status_label === 'correction' && <Pill tone="accent">CORRECTION</Pill>}
                    {st.superseded && <Pill tone="warn">SUPERSEDED</Pill>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 24 }}>
                  <button className="btn-link" onClick={() => navigate(`/statements/${st.id}`)}>View online</button>
                  <a className="btn-link"
                     href={`/api/v1/statements/${st.id}/pdf`}
                     download
                     style={{ textDecoration: 'none' }}>
                    Download PDF ↓
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Don't flash the empty-state at a fund investor before their fund poll
          resolves — the SMA fetch returns [] fast and would otherwise win the
          race and show "no statements yet" for a render (adversarial LOW #3). */}
      {!hasSMA && !hasFund && (!isInvestor || fundRows !== null) && (
        <p className="serif-body" style={{ color: 'var(--ink-2)' }}>
          No statements yet. The first one issues at the close of the current period.
        </p>
      )}

      <section style={{ marginTop: 64 }}>
        <div style={{ maxWidth: '76ch' }}>
          <div className="label" style={{ marginBottom: 12 }}>IMMUTABILITY</div>
          <p className="serif-body" style={{ margin: 0, fontSize: 16 }}>
            This statement is a final record for the period and is not subject to revision. Corrections, if necessary, are issued as a new statement that supersedes — but does not replace — the original.
          </p>
        </div>
      </section>
    </div>
  );
}
